<template>
  <div class="shadow-xs rounded">
    <div class="shadow-md rounded bg-white flex flex-col overflow-hidden">
      <div class="inline-flex px-4 h-12 items-center">
        <span class="inline-block truncate text-xs">
          {{ name }}
        </span>
      </div>
      <a
        :href="url"
        target="_blank"
        class="relative bg-gray-100"
        style="padding-bottom: 66.666667%"
      >
        <img v-if="cover" :src="cover" class="absolute w-full h-full object-cover" />
        <div v-else class="absolute w-full h-full inline-flex items-center justify-center">
          <div class="flex flex-col items-center space-y-4">
            <svg viewBox="0 0 24 24" fill="currentColor" class="h-12 w-12 text-gray-600">
              <path
                v-if="note"
                fill="currentColor"
                class="text-yellow-600"
                d="M21 15l-6 5.996L4.002 21A.998.998 0 0 1 3 20.007V3.993C3
                3.445 3.445 3 3.993 3h16.014c.548 0 .993.456.993 1.002V15zM19
                5H5v14h8v-5a1 1 0 0 1 .883-.993L14 13l5-.001V5zm-.829
                9.999L15 15v3.169l3.171-3.17z"
              />
              <path
                v-else-if="link"
                d="M10 6v2H5v11h11v-5h2v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1
                1 0 0 1 1-1h6zm11-3v8h-2V6.413l-7.793
                7.794-1.414-1.414L17.585 5H13V3h8z"
              />
              <path
                v-else-if="isPDF"
                fill="currentColor"
                class="text-red-600"
                d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9
                2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5
                1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67
                1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5
                1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4
                6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"
              />
              <path
                v-else
                d="M12 16H8V8h4a4 4 0 1 1 0 8zm-2-6v4h2a2 2 0 1 0
                0-4h-2zm5-6H5v16h14V8h-4V4zM3 2.992C3 2.444 3.447
                2 3.999 2H16l5 5v13.993A1 1 0 0 1 20.007 22H3.993A1
                1 0 0 1 3 21.008V2.992z"
              />
            </svg>
            <p class="text-gray-900">No preview available</p>
          </div>
        </div>
        <ProjectAssetApprovalStatus
          v-if="hasApprovalRequests || hasApprovalClaims"
          :claims="projectAsset.approvalClaimsCount"
          :total="projectAsset.approvalRequestsCount"
          :pending="projectAsset.pendingApprovalRequestsCount"
          :approved="projectAsset.approvedApprovalRequestsCount"
          :rejected="projectAsset.rejectedApprovalRequestsCount"
          class="absolute top-0 left-0 mt-4 ml-4"
        />
      </a>
      <div class="flex flex-col space-y-2 px-4 py-3">
        <ProjectAssetApprovalManager :project-asset="projectAsset" />
        <ProjectAssetRejectMessages
          v-if="hasRejectedApprovalRequests"
          :project-asset="projectAsset"
        />
        <ProjectAssetDeleteAction
          v-if="!hasMailedApprovalRequests && !hasApprovedApprovalRequests"
          :project-asset="projectAsset"
          @project-asset-deleted="reload"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ProjectAssetApprovalStatus from './ProjectAssetApprovalStatus.vue';
import ProjectAssetApprovalManager from './ProjectAssetApprovalManager.vue';
import ProjectAssetRejectMessages from './ProjectAssetRejectMessages.vue';
import ProjectAssetDeleteAction from './ProjectAssetDeleteAction.vue';

export default {
  components: {
    ProjectAssetApprovalStatus,
    ProjectAssetApprovalManager,
    ProjectAssetRejectMessages,
    ProjectAssetDeleteAction,
  },
  props: {
    asset: {
      type: Object,
      required: true,
    },
    projectAsset: {
      type: Object,
      default: null,
    },
  },
  inject: ['reload'],
  computed: {
    hasApprovalRequests() {
      if (!this.projectAsset) {
        return false;
      }

      if (!this.projectAsset.approvalRequestsCount) {
        return false;
      }

      return this.projectAsset.approvalRequestsCount > 0;
    },
    hasApprovalClaims() {
      if (!this.projectAsset) {
        return false;
      }

      if (!this.projectAsset.approvalClaimsCount) {
        return false;
      }

      return this.projectAsset.approvalClaimsCount > 0;
    },
    hasRejectedApprovalRequests() {
      if (!this.projectAsset) {
        return false;
      }

      return (this.projectAsset?.rejectedApprovalRequestsCount || 0) > 0;
    },
    hasApprovedApprovalRequests() {
      if (!this.projectAsset) {
        return false;
      }

      return (this.projectAsset?.approvedApprovalRequestsCount || 0) > 0;
    },
    hasMailedApprovalRequests() {
      if (!this.projectAsset) {
        return false;
      }

      return (this.projectAsset?.mailedApprovalRequestsCount || 0) > 0;
    },
    media() {
      if (!this.asset.media) {
        return null;
      }

      return this.$store.state.entities.media.byId[this.asset.media];
    },
    isPDF() {
      return this.media && this.media.mimeType && this.media.mimeType === 'application/pdf';
    },
    link() {
      if (!this?.asset?.link) {
        return null;
      }

      return this.$store.state.entities.links.byId[this.asset.link];
    },
    note() {
      if (!this?.asset?.note) {
        return null;
      }

      return this.$store.state.entities.notes.byId[this.asset.note];
    },
    name() {
      if (this.media) {
        return this.media.name;
      }

      if (this.link) {
        return this.link.title || this.link.url;
      }

      return 'Note';
    },
    url() {
      if (this.media) {
        return this.media.url;
      }

      if (this.link) {
        return this.link.url;
      }

      return '#0';
    },
    cover() {
      if (this.media && this.media.mimeType && this.media.mimeType.indexOf('image') === 0) {
        return this.media.url;
      }

      if (this.link && this.link.cover) {
        return this.link.cover;
      }

      return null;
    },
  },
};
</script>
